import { RECIPES, type Recipe } from './crafting';
import { ITEMS, type PlayerInventory } from '../game/inventory';
import { SK, redHover } from './skin';

// PINNED RECIPES (owner QoL): pin up to 3 recipes in the craft menu and a small top-right checklist
// follows you around -- each ingredient shows have/need and ticks green as you farm it. Dismiss per-pin
// (✕) or by unpinning in the menu. Persists across reloads.

const KEY = 'oxide.pinnedRecipes';
const MAX_PINS = 3;

export class PinnedHud {
  private readonly root: HTMLDivElement;
  private pins: string[] = [];
  onChange: (() => void) | null = null; // craft menu repaints its pin buttons

  constructor(private readonly inv: PlayerInventory) {
    try { this.pins = (JSON.parse(localStorage.getItem(KEY) ?? '[]') as string[]).slice(0, MAX_PINS); } catch { /* fresh */ }
    this.root = document.createElement('div');
    this.root.style.cssText = 'position:fixed;top:14px;right:14px;z-index:9;display:flex;flex-direction:column;gap:8px;'
      + `font-family:${SK.font};pointer-events:none`;
    document.body.appendChild(this.root);
    this.render();
  }

  has(id: string): boolean { return this.pins.includes(id); }

  // Toggle a pin (max 3: pinning a 4th drops the oldest). Returns whether the id is now pinned.
  toggle(id: string): boolean {
    const i = this.pins.indexOf(id);
    if (i >= 0) this.pins.splice(i, 1);
    else { this.pins.push(id); while (this.pins.length > MAX_PINS) this.pins.shift(); }
    this.save();
    this.render();
    this.onChange?.();
    return this.pins.includes(id);
  }

  refresh(): void { this.render(); } // call on inventory change

  private save(): void { try { localStorage.setItem(KEY, JSON.stringify(this.pins)); } catch { /* ignore */ } }

  private render(): void {
    this.root.replaceChildren();
    for (const id of this.pins) {
      const r: Recipe | undefined = RECIPES.find((x) => x.id === id);
      if (!r) continue;
      // Chip look (glass + hairline, no blur -- these float over the 3D canvas all session).
      const card = document.createElement('div');
      card.style.cssText = `background:${SK.glass};border:1px solid ${SK.border};border-radius:${SK.rSm};`
        + 'padding:8px 12px;min-width:150px;pointer-events:auto;box-shadow:0 4px 16px rgba(0,0,0,0.4)';
      const head = document.createElement('div');
      head.style.cssText = 'display:flex;align-items:center;gap:6px;margin-bottom:3px';
      const title = document.createElement('span');
      title.textContent = '📌 ' + (ITEMS[r.id]?.name ?? r.id);
      title.style.cssText = `flex:1;font:700 ${SK.fsSmall} ${SK.font};color:${SK.textHi}`;
      const x = document.createElement('button');
      x.textContent = '✕';
      x.title = 'Unpin';
      x.style.cssText = `background:transparent;border:none;color:${SK.dim};cursor:pointer;font:700 ${SK.fsMicro} ${SK.font};padding:0`;
      x.addEventListener('click', () => this.toggle(id));
      redHover(x);
      head.append(title, x);
      card.appendChild(head);
      for (const c of r.cost) {
        const have = this.inv.count(c.item);
        const ok = have >= c.amount;
        const line = document.createElement('div');
        line.style.cssText = `font:${SK.fsMicro}/1.5 ${SK.font};font-variant-numeric:tabular-nums;color:${ok ? SK.good : SK.text}`;
        line.textContent = `${ok ? '✓' : '·'} ${ITEMS[c.item]?.name ?? c.item}  ${Math.min(have, c.amount)}/${c.amount}`;
        card.appendChild(line);
      }
      this.root.appendChild(card);
    }
  }
}
